/**
 * Add-to-cart feedback for the product page.
 *
 * Owns the /cart/add.js request so that Rebuy's drawer can be kept shut, the
 * product image can be flown into the header cart icon, and the result can be
 * announced below the button.
 *
 * @author Maksym Mariukhnik
 */
window.PSAtc = (function () {
  'use strict';

  var FLIGHT_MS = 780;
  var MESSAGE_MS = 10000;
  var RELEASE_GRACE_MS = 800;
  var MAX_QUANTITY = 99;

  function isVisible(el) {
    if (!el) return false;
    var rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  function firstVisible(selector) {
    var nodes = document.querySelectorAll(selector);
    for (var i = 0; i < nodes.length; i++) {
      if (isVisible(nodes[i])) return nodes[i];
    }
    return null;
  }

  /** The header renders two cart links; one of them is always hidden. */
  function cartIcon() {
    return firstVisible('cart-icon, .action__cart');
  }

  /** Mobile shows a Swiper slide, desktop an image grid. Whichever is on screen wins. */
  function productImage() {
    // The PDP gallery is one carousel now (.ps-gallery__main). The old
    // .mobile-gallery / .desktop-gallery pair no longer exists, and looking for
    // it returned null, so flyToCart() exited before animating anything.
    return firstVisible('.ps-gallery__main .swiper-slide-active img') ||
           firstVisible('.ps-gallery__main img');
  }

  /**
   * Keep the Rebuy Smart Cart drawer from auto-opening when we add to cart.
   *
   * Rebuy exposes an official flag for exactly this: SmartCart.skip_open. When
   * true, Rebuy does not open the drawer on add, but an explicit
   * SmartCart.show() still opens it - the header cart icon, and the
   * related-products / you-might-like sections that call show() themselves,
   * keep working. Verified on the live theme: with the flag set, auto-open is
   * suppressed on every add (0/12) and the icon still opens; without it the
   * drawer opens on every add (6/6).
   *
   * This replaces an earlier approach that monkey-patched SmartCart.show().
   * That raced with Rebuy's own async show() call and let the drawer slip open
   * roughly one add in eight. skip_open is read by Rebuy at the moment it
   * decides to open, so there is no race and no restore timing to get wrong.
   *
   * We only ever call this from the product page's add flow, and the flag
   * resets on the next full page load, so it never leaks to collection pages.
   *
   * The flag must be set on EVERY add, in both directions: the main button and
   * the sticky bar share one page and one Rebuy instance. If the main button
   * (suppressed) set it true and we only ever set true, a later sticky add
   * (which wants the drawer to open) would inherit the stale true and stay
   * shut. So each add writes the flag it needs, explicitly.
   */
  var releaseTimer = null;

  function setDrawerSuppressed(suppressed) {
    var smartCart = window.Rebuy && window.Rebuy.SmartCart;
    if (!smartCart) return;
    // A pending release must not fire into the add we are about to make.
    clearTimeout(releaseTimer);
    smartCart.skip_open = !!suppressed;
  }

  /**
   * Hand auto-open back to the rest of the page once our add has settled.
   *
   * skip_open is global on the Rebuy instance, so leaving it true would follow
   * the shopper around the product page: every add that depends on Rebuy's
   * AUTO-open - Rebuy's own cards and anything that does not call
   * SmartCart.show() itself - would silently stop opening the drawer.
   *
   * The release waits RELEASE_GRACE_MS after our request settles. Rebuy decides
   * whether to auto-open from its own patched fetch, right after the add
   * response lands - well before our chain (which also refetches /cart.js) is
   * done - so by the time we hand the flag back its decision is already made.
   */
  function releaseDrawerSuppression() {
    clearTimeout(releaseTimer);
    releaseTimer = setTimeout(function () {
      var smartCart = window.Rebuy && window.Rebuy.SmartCart;
      if (smartCart) smartCart.skip_open = false;
    }, RELEASE_GRACE_MS);
  }

  /**
   * Refresh the theme's header cart badge.
   *
   * The visible count is Horizon's <cart-icon> web component, which only
   * updates from a 'cart:update' event carrying detail.data.itemCount. Rebuy's
   * own "Item Count" theme selector is set to .cart-count, which does not exist
   * in this Horizon theme (0 matches), so Rebuy cannot move the badge - this
   * event is what does, and it is the same mechanism the collection/related
   * sections already use.
   *
   * itemCount is the absolute cart total from /cart.js. We deliberately omit
   * source: 'product-form-component': that value makes <cart-icon> ADD itemCount
   * to its current value; without it the component SETS the absolute count.
   */
  function updateCartBadge(cart) {
    if (!cart || typeof cart.item_count !== 'number') return;
    var event;
    try {
      event = new CustomEvent('cart:update', {
        bubbles: true,
        detail: { resource: cart, data: { itemCount: cart.item_count } }
      });
    } catch (e) {
      return;
    }
    document.dispatchEvent(event);
  }

  /**
   * How many units the shopper asked for.
   *
   * Both add-to-cart entry points post the SAME form - the main button submits
   * it, the sticky bar hands it over - so reading the stepper here is what makes
   * one quantity serve both. Reading goes through parseInt, so a fractional or
   * negative entry lands on the nearest sane whole number ("1.5" adds 1, "-3"
   * adds 1) instead of being re-spelled into a bigger one: a bad value must
   * never turn into a bigger order than the shopper meant.
   *
   * The upper bound is a typo guard only. Real stock limits stay with Shopify,
   * which answers an over-add with "You can only add X to the cart", and that
   * message is already surfaced by announce().
   *
   * @param {HTMLFormElement} form
   * @returns {number} a whole number between 1 and MAX_QUANTITY.
   */
  function readQuantity(form) {
    var input = form.querySelector('.ps-qty__input');
    return input ? clampQuantity(input.value) : 1;
  }

  function clampQuantity(value) {
    var quantity = parseInt(value, 10);
    if (!isFinite(quantity) || quantity < 1) return 1;
    return Math.min(quantity, MAX_QUANTITY);
  }

  /**
   * Puts the stepper back to 1 after a successful add, so a second click on
   * either button cannot silently repeat the previous quantity.
   *
   * @param {HTMLFormElement} form
   */
  function resetQuantity(form) {
    var input = form && form.querySelector('.ps-qty__input');
    if (!input || input.value === '1') return;

    input.value = '1';
    // The stepper's own listeners keep the +/- disabled state in sync off this.
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Mirrors a settled quantity onto every stepper on the page and refreshes the
   * +/- disabled states.
   *
   * The buy box and the sticky bar each render a stepper but share one quantity,
   * so whichever one the shopper touches, the other has to follow. Assigning
   * .value fires no events, so this cannot loop back into the listeners below.
   *
   * @param {number} quantity - already clamped.
   * @param {HTMLInputElement} [source] - the input the shopper edited; left alone
   *   so an in-progress edit is not re-written under the caret.
   */
  function syncQuantity(quantity, source) {
    var inputs = document.querySelectorAll('.ps-qty__input');
    for (var i = 0; i < inputs.length; i++) {
      if (inputs[i] !== source) inputs[i].value = String(quantity);
    }

    var buttons = document.querySelectorAll('.ps-qty__btn');
    for (var j = 0; j < buttons.length; j++) {
      var step = parseInt(buttons[j].getAttribute('data-qty-step'), 10);
      buttons[j].disabled = step < 0 ? quantity <= 1 : quantity >= MAX_QUANTITY;
    }

    // The Add to Cart button spells out what the click will actually add, so it
    // has to follow the quantity. Announced rather than called directly: the
    // steppers are delegated from the document and the label lives in the
    // product section, which owns pricing.
    document.dispatchEvent(new CustomEvent('ps:quantity', { detail: { quantity: quantity } }));
  }

  function isQuantityInput(el) {
    return !!(el && el.classList && el.classList.contains('ps-qty__input'));
  }

  /**
   * Wires the +/- buttons and keeps the field honest.
   *
   * Delegated from the document rather than bound per stepper: the sticky bar's
   * copy and the buy box's are the same control, and StayAI/Rebuy re-render
   * around them, so nothing here depends on when a stepper enters the DOM.
   */
  function initQuantitySteppers() {
    document.addEventListener('click', function (event) {
      var button = event.target.closest && event.target.closest('.ps-qty__btn');
      if (!button) return;

      var box = button.closest('.ps-qty');
      var input = box && box.querySelector('.ps-qty__input');
      if (!input) return;

      var step = parseInt(button.getAttribute('data-qty-step'), 10) || 0;
      var quantity = clampQuantity(clampQuantity(input.value) + step);
      input.value = String(quantity);
      syncQuantity(quantity, input);
    });

    // While typing, only ever correct the field DOWNWARDS, and only when the
    // number is over the cap, so "150" turns into "99" as it is typed.
    //
    // Everything else is left alone until the field is left. Stripping the
    // non-digits here instead would silently re-spell an entry into a larger
    // one - "1.5" becomes "15", fifteen units the shopper never asked for -
    // whereas settle() reads it through parseInt and lands on 1. An empty box
    // is tolerated too: rewriting it to "1" under the caret makes replacing
    // "1" with "12" a fight.
    document.addEventListener('input', function (event) {
      var input = event.target;
      if (!isQuantityInput(input)) return;
      if (input.value.trim() === '') return;

      if (parseInt(input.value, 10) > MAX_QUANTITY) {
        input.value = String(MAX_QUANTITY);
      }
      syncQuantity(clampQuantity(input.value), input);
    });

    // Settle on a real number when the field is left, and pick up programmatic
    // writes (resetQuantity). focusout is used because blur does not bubble.
    function settle(event) {
      var input = event.target;
      if (!isQuantityInput(input)) return;

      var quantity = clampQuantity(input.value);
      input.value = String(quantity);
      syncQuantity(quantity, input);
    }

    document.addEventListener('change', settle);
    document.addEventListener('focusout', settle);

    // Initial pass so "-" starts disabled at 1 and both copies agree on load.
    var first = document.querySelector('.ps-qty__input');
    if (first) syncQuantity(clampQuantity(first.value));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initQuantitySteppers);
  } else {
    initQuantitySteppers();
  }

  /**
   * Reads the variant and, for a subscription, the StayAI plan id.
   *
   * The authoritative signal is the purchaseType radio, NOT the plan <select>
   * value: Safari / iOS WebKit can restore the <select> from autofill/bfcache to
   * a stale plan id even while One-Time is selected, which would silently turn a
   * one-time order into a subscription.
   */
  function buildPayload(form) {
    var variantIdInput = form.querySelector('.variant-id-input');
    if (!variantIdInput || !variantIdInput.value) return null;

    var payload = { id: variantIdInput.value, quantity: readQuantity(form) };

    var purchaseType = form.querySelector('input[name="purchaseType"]:checked');
    var isSubscription = !!(purchaseType && /subscription/i.test(purchaseType.value));
    var sellingPlanSelect = form.querySelector('select[name="stayai_selling_plan"]');
    if (isSubscription && sellingPlanSelect && sellingPlanSelect.value) {
      payload.selling_plan = sellingPlanSelect.value;
    }

    return payload;
  }

  /**
   * @param {HTMLFormElement} form - the .product-form to read the variant/plan from.
   * @param {{ openDrawer?: boolean }} [options] - when openDrawer is true, let
   *   Rebuy auto-open its drawer on the add (the sticky bar's behaviour). When
   *   false or omitted, suppress the drawer so the flight + message carry the
   *   feedback instead (the main Add to Cart button).
   */
  function addToCart(form, options) {
    var payload = buildPayload(form);
    if (!payload) return Promise.reject(new Error('Please choose an option first.'));

    // Written every time, both ways: openDrawer:true (sticky) lets Rebuy open,
    // openDrawer false/omitted (main button) keeps it shut. See setDrawerSuppressed.
    setDrawerSuppressed(!(options && options.openDrawer));

    var request = fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) {
          throw new Error(data.description || data.message || 'Could not add to cart. Please try again.');
        }
        return data;
      }, function () {
        // The body wasn't valid JSON (e.g. Shopify or a proxy returned an
        // HTML error page for a 5xx). Suppress the raw JSON SyntaxError so
        // it never becomes the message shown to the shopper.
        throw new Error('Could not add to cart. Please try again.');
      });
    }).then(function (data) {
      // The add response is a single line item with no cart total. Refetch the
      // cart for the true count and refresh the header badge. A failed refetch
      // must not fail the add - the item is already in the cart.
      return fetch('/cart.js').then(function (response) {
        return response.json();
      }).then(function (cart) {
        updateCartBadge(cart);
        return data;
      }, function () {
        return data;
      });
    });

    // Both handlers are supplied, so this branch never leaves an unhandled
    // rejection; the caller still gets `request` and its own .catch() runs.
    request.then(releaseDrawerSuppression, releaseDrawerSuppression);

    return request;
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /** The badge is only rendered once the cart has contents. */
  function bumpCartIcon(icon) {
    icon.animate(
      [{ transform: 'translateY(0) scale(1)' },
       { transform: 'translateY(-5px) scale(1.14)' },
       { transform: 'translateY(0) scale(1)' }],
      { duration: 420, easing: 'cubic-bezier(.34,1.56,.64,1)' }
    );

    var bubble = icon.querySelector('.cart-bubble');
    if (bubble && isVisible(bubble)) {
      bubble.animate(
        [{ transform: 'scale(1)' }, { transform: 'scale(1.55)' }, { transform: 'scale(1)' }],
        { duration: 380, easing: 'cubic-bezier(.34,1.56,.64,1)' }
      );
    }
  }

  /**
   * Arc flight: the outer element carries horizontal travel, the inner one
   * carries vertical travel plus scale and rotation. Splitting the two axes is
   * what bends the path into an arc.
   */
  /**
   * @param {HTMLImageElement} sourceImage - the product image to clone.
   * @param {Element} [originEl] - where the flight starts from. The client wants
   *   the image to launch from the Add to Cart button, so the caller passes the
   *   button here. Without it, the flight starts from the image's own position.
   */
  function flyToCart(sourceImage, originEl) {
    var target = cartIcon();
    var origin = originEl || sourceImage;
    if (!sourceImage || !origin || !target || prefersReducedMotion()) return;

    var originRect = origin.getBoundingClientRect();
    var to = target.getBoundingClientRect();
    if (!originRect.width || !to.width) return;

    // Launch a small square clone from the centre of the origin. A full-size
    // clone of the gallery image would swamp the button; a compact square reads
    // as the product leaping out of the button and into the cart.
    var startSize = Math.max(48, Math.min(originRect.height * 1.6, 90));
    var from = {
      left: originRect.left + originRect.width / 2 - startSize / 2,
      top: originRect.top + originRect.height / 2 - startSize / 2,
      width: startSize,
      height: startSize
    };

    var dx = (to.left + to.width / 2) - (from.left + from.width / 2);
    var dy = (to.top + to.height / 2) - (from.top + from.height / 2);
    var endScale = Math.max(to.width / from.width, 0.12);

    var flyer = document.createElement('div');
    flyer.className = 'ps-atc-flyer';
    flyer.style.width = from.width + 'px';
    flyer.style.height = from.height + 'px';
    flyer.style.transform = 'translate(' + from.left + 'px, ' + from.top + 'px)';

    var inner = document.createElement('div');
    inner.className = 'ps-atc-flyer__inner';

    var clone = document.createElement('img');
    clone.src = sourceImage.currentSrc || sourceImage.src;
    clone.alt = '';
    inner.appendChild(clone);
    flyer.appendChild(inner);
    document.body.appendChild(flyer);

    flyer.animate(
      [{ transform: 'translate(' + from.left + 'px, ' + from.top + 'px)' },
       { transform: 'translate(' + (from.left + dx) + 'px, ' + from.top + 'px)' }],
      { duration: FLIGHT_MS, easing: 'cubic-bezier(.4,0,.9,.5)', fill: 'forwards' }
    );

    inner.animate(
      [{ transform: 'translateY(0) scale(1) rotate(0deg)', opacity: 1 },
       { transform: 'translateY(' + (dy * 0.42) + 'px) scale(0.62) rotate(-7deg)', opacity: 1, offset: 0.55 },
       { transform: 'translateY(' + dy + 'px) scale(' + endScale + ') rotate(-14deg)', opacity: 0.25 }],
      { duration: FLIGHT_MS, easing: 'cubic-bezier(.2,.6,.35,1)', fill: 'forwards' }
    );

    // Chrome never resolves animation.finished in a background tab, which would
    // strand the clone on top of the page. A timer always runs.
    var landed = false;
    setTimeout(function () {
      if (landed) return;
      landed = true;
      flyer.parentNode && flyer.parentNode.removeChild(flyer);
      bumpCartIcon(target);
    }, FLIGHT_MS + 40);
  }

  var messageTimers = new WeakMap();

  /**
   * Renders into the live region below the Add to Cart button. A repeat add
   * restarts the ten-second window; an error stays until the next attempt.
   */
  function announce(root, state, text) {
    var box = root && root.querySelector('[data-atc-message]');
    if (!box) return;

    var label = box.querySelector('.atc-message__text');
    if (!label) return;

    clearTimeout(messageTimers.get(box));

    label.textContent = text;
    box.classList.toggle('atc-message--error', state === 'error');
    box.classList.add('is-visible');

    if (state === 'added') {
      messageTimers.set(box, setTimeout(function () {
        box.classList.remove('is-visible');
      }, MESSAGE_MS));
    }
  }

  return {
    addToCart: addToCart,
    resetQuantity: resetQuantity,
    flyToCart: flyToCart,
    announce: announce,
    cartIcon: cartIcon,
    productImage: productImage
  };
})();
